import { useState } from "react"
import { navigate } from "vike/client/router"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Spinner from "aldoo-ra/Spinner"
import { TrashIcon, UserCircleIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline"
import { url as cmsUrl } from "aldoo-ra/CMS/url"
import Tooltip from "aldoo-ra/Tooltip"

const AVATAR_COLORS = [
  "#e53935", "#d81b60", "#8e24aa", "#5e35b1",
  "#1e88e5", "#039be5", "#00897b", "#43a047",
  "#fb8c00", "#f4511e", "#6d4c41", "#546e7a",
]

function getAvatarColor(str) {
  if (!str) return AVATAR_COLORS[0]
  let hash = 0
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash)
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

function ContactAvatar({ contact }) {
  const [imgError, setImgError] = useState(false)
  const avatarSrc = contact.avatar && !imgError ? cmsUrl(contact.avatar) : null
  const initial = (contact.name || "?")[0].toUpperCase()
  const color = getAvatarColor(contact.userId || contact.name)

  if (avatarSrc) {
    return (
      <img
        src={avatarSrc}
        alt=""
        onError={() => setImgError(true)}
        className="w-6 sm:w-10 h-6 sm:h-10 rounded-full object-cover flex-shrink-0 border border-bordered"
      />
    )
  }

  return (
    <div
      className="w-6 sm:w-10 h-6 sm:h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white font-semibold text-sm"
      style={{ background: color }}
    >
      {initial}
    </div>
  )
}

export default function ContactRow({ contact, onRemove }) {
  const [removing, setRemoving] = useState(false)
  const [sending, setSending] = useState(false)

  const handleRemove = async () => {
    setRemoving(true)
    try {
      await onRemove(contact.userId)
    } finally {
      setRemoving(false)
    }
  }

  const doSend = () => {
    if (!contact.email || sending) return
    setSending(true)
    sessionStorage.setItem(
      'transfer:initialAccess',
      JSON.stringify({ emails: [contact.email], type: 'authenticated' }),
    )
    navigate('/')
  }

  const infoSection = (
    <div className="flex items-center gap-3">
      <ContactAvatar contact={contact} />
      <div className="flex flex-col min-w-0 gap-0.5">
        <Typography variant="txt1_accent" className="block text-text truncate leading-tight">
          {contact.name || "Unknown"}
        </Typography>
        {contact.email && (
          <Typography variant="txt2_dim" className="block leading-tigh truncate">
            {contact.email}
          </Typography>
        )}
      </div>
    </div>
  )

  return (
    <div
      className={`flex flex-wrap sm:flex-nowrap items-center gap-1 sm:gap-3 sm:px-5 py-3 transition-colors ${contact.email ? 'cursor-pointer hover:bg-neutral_1/40' : ''}`}
      onClick={doSend}
    >
      <div className="min-w-0 w-full sm:w-auto sm:flex-1">
        {contact.email ? (
          <Tooltip text="Share files" variant="dark" position="left" display="block">
            {infoSection}
          </Tooltip>
        ) : infoSection}
      </div>

      <div className="flex items-center gap-1 ml-auto sm:ml-0">
        {contact.email && (
          <Tooltip text="Share files" variant="dark" position="top">
            <Button
              variant="icon"
              disabled={sending}
              onClick={(e) => { e.stopPropagation(); doSend() }}
              className="hover:text-primary"
            >
              {sending ? <Spinner variant="minimal" /> : <PaperAirplaneIcon className="w-4 h-4" />}
            </Button>
          </Tooltip>
        )}

        <Tooltip text="Remove contact" variant="dark" position="top">
          <Button
            variant="icon"
            onClick={(e) => { e.stopPropagation(); handleRemove() }}
            disabled={removing}
            className="hover:text-error"
          >
            <TrashIcon className="w-4 h-4" />
          </Button>
        </Tooltip>
      </div>
    </div>
  )
}
